import type { SupabaseClient } from '@supabase/supabase-js';

type ChildTableConfig = {
  table: string;
  column?: string;
  label: string;
};

type ChildCountResult = {
  table: string;
  label: string;
  count: number;
};

type CheckResult = {
  canDelete: boolean;
  message: string;
  details: ChildCountResult[];
};

// Uses untyped SupabaseClient because child tables are resolved at runtime
// from dynamic strings — the typed Database generic requires literal table names.
export const checkChildrenBeforeDelete = async (
  supabase: SupabaseClient,
  parentId: string | null | undefined,
  childTables: ChildTableConfig[] = []
): Promise<CheckResult> => {
  if (!parentId || !childTables || childTables.length === 0) {
    return { canDelete: true, message: '', details: [] };
  }

  try {
    const childCounts = await Promise.all(
      childTables.map(async ({ table, column, label }) => {
        // Se column não foi especificado, usa o padrão da FK
        const fkColumn = column || `${table.split('_')[0]}_id`;

        const { count, error } = await supabase
          .from(table)
          .select('id', { count: 'exact', head: true })
          .eq(fkColumn, parentId)
          .is('deleted_at', null);

        if (error) {
          // Propagate: unverifiable dependencies must block delete, not silently pass.
          console.error(`[checkChildrenBeforeDelete] Falha ao verificar ${table}:`, error);
          throw new Error(`Falha ao verificar ${label}: ${error.message || 'erro desconhecido'}`);
        }

        return { table, label, count: count || 0 };
      })
    );

    const activeChildren = childCounts.filter(c => c.count > 0);

    if (activeChildren.length > 0) {
      const details = activeChildren.map(c => `${c.count} ${c.label}`).join(', ');
      return {
        canDelete: false,
        message: `Não é possível excluir: existem ${details} vinculados. Exclua-os primeiro.`,
        details: activeChildren
      };
    }

    return { canDelete: true, message: '', details: [] };
  } catch (error) {
    console.error('[checkChildrenBeforeDelete] Erro:', error);
    return {
      canDelete: false,
      message: 'Erro ao verificar dependências. Tente novamente.',
      details: []
    };
  }
};

/**
 * Configurações de tabelas filhas para entidades comuns
 * Use estas configurações prontas nos drawers
 */
export const CHILD_TABLES_CONFIG = {
  // ambiente/ponto-scoped children (obra_ambiente via obra_area_id, obra_cabo/produto/servico
  // via obra_ponto_id) are gated transitively by the obra_area and obra_ponto checks below —
  // schema forbids orphan descendants without at least one area or ponto.
  obra: [
    { table: 'obra_area', column: 'obra_id', label: 'área(s)' },
    { table: 'obra_ponto', column: 'obra_id', label: 'ponto(s)' },
    { table: 'projeto', column: 'obra_id', label: 'projeto(s)' },
    { table: 'obra_tubo', column: 'obra_id', label: 'tubo(s)' },
    { table: 'obra_jornada', column: 'obra_id', label: 'jornada(s)' },
    { table: 'obra_envolvido', column: 'obra_id', label: 'envolvido(s)' },
    { table: 'obra_andamento', column: 'obra_id', label: 'andamento(s)' }
  ],

  obra_area: [
    { table: 'obra_ambiente', column: 'obra_area_id', label: 'ambiente(s)' }
  ],

  obra_ambiente: [
    { table: 'obra_ponto', column: 'obra_ambiente_id', label: 'ponto(s)' }
  ],

  obra_ponto: [
    { table: 'obra_produto', column: 'obra_ponto_id', label: 'produto(s)' },
    { table: 'obra_servico', column: 'obra_ponto_id', label: 'serviço(s)' }
  ],

  pessoa: [
    { table: 'oportunidade', column: 'pessoa_id', label: 'oportunidade(s)' },
    { table: 'proposta', column: 'pessoa_id', label: 'proposta(s)' },
    { table: 'obra_envolvido', column: 'pessoa_id', label: 'vínculo(s) com obra' },
    { table: 'lancamento', column: 'parte_id', label: 'lançamento(s)' }
  ],

  oportunidade: [
    { table: 'proposta', column: 'oportunidade_id', label: 'proposta(s)' }
  ],

  empreendimento: [
    { table: 'obra', column: 'empreendimento_id', label: 'obra(s)' }
  ],

  departamento: [
    { table: 'pessoa', column: 'departamento_id', label: 'pessoa(s)' }
  ],

  categoria_contabil: [
    { table: 'lancamento', column: 'categoria_contabil_id', label: 'lançamento(s)' }
  ],

  centro_custo: [
    { table: 'lancamento', column: 'centro_custo_id', label: 'lançamento(s)' }
  ],

  conta: [
    { table: 'lancamento', column: 'conta_id', label: 'lançamento(s)' }
  ]

  // os: removido — fn_soft_delete_os faz cascade completo (filhos + netos)
};

export default checkChildrenBeforeDelete;
